import type { Page, PageMessage, Picklists, Search, Team } from "@pagelink/shared";

export interface Actor {
  id: string; name: string; title: string; service: string; siteId: string; isAdmin: boolean;
  /** Primary phone, prefilled as the callback on each page. */
  callback: string | null;
  phoneAlt: string | null;
  email: string | null;
}
export interface OnCallEntry {
  service: string; sub_service: string | null; position: number; role_note: string | null;
  id: string; name: string; title: string;
}

/** One rota row as the calendar sees it. A row can span several days. */
export interface ScheduleShift {
  rowId: string; service: string; subService: string | null; position: number;
  startsAt: string; endsAt: string; roleNote: string | null;
  shift: string | null; clinicianId: string; name: string; title: string;
}

export interface AdminPerson {
  id: string; name: string; title: string; service: string; isAdmin: boolean;
  email: string | null; phone: string | null; disabled: boolean; upn: string | null; hasPasskey: boolean;
}
export interface AdminRotaRow {
  id: string; service: string; subService: string | null; position: number; shiftId: string | null; shift: string | null;
  startsAt: string; endsAt: string; clinicianId: string; name: string;
}
export interface AdminShift { id: string; label: string; starts: string; ends: string }

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly data?: unknown) {
    super(message);
  }
}

/* Inside a Teams tab the session rides in a bearer header, not a cookie:
 * the tab is an iframe on teams.microsoft.com, where third-party cookies are
 * not reliable. Kept for the life of the tab. */
const BEARER_KEY = "pagecom.session";
let bearer: string | null = (() => { try { return sessionStorage.getItem(BEARER_KEY); } catch { return null; } })();
export function setBearer(token: string | null): void {
  bearer = token;
  try { if (token) sessionStorage.setItem(BEARER_KEY, token); else sessionStorage.removeItem(BEARER_KEY); } catch { /* fine */ }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: {
      ...(init?.body === undefined ? {} : { "content-type": "application/json" }),
      ...(bearer ? { authorization: `Bearer ${bearer}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : {};
  if (!res.ok) throw new ApiError(body.error ?? `Request failed (${res.status})`, res.status, body);
  return body as T;
}

/** A file from the API, with the same credentials as every other call. */
export async function fetchBlob(path: string): Promise<Blob> {
  const res = await fetch(path, { credentials: "same-origin", headers: bearer ? { authorization: `Bearer ${bearer}` } : {} });
  if (!res.ok) throw new ApiError(`Request failed (${res.status})`, res.status, {});
  return res.blob();
}

const post = <T>(path: string, body?: unknown) =>
  call<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });

export interface DevClinician { id: string; name: string; title: string; service: string }

export const api = {
  me: () => call<{ actor: Actor; vapidPublicKey: string }>("/api/me"),
  authConfig: () => call<{ devLogin: boolean; teams: boolean; patientData?: boolean }>("/api/auth/config"),
  teamsVerify: (token: string) => post<{ ok: true; session: string }>("/api/auth/teams/verify", { token }),
  devClinicians: () => call<{ clinicians: DevClinician[] }>("/api/auth/dev/clinicians"),
  devLogin: (clinicianId: string) => post<{ ok: true }>("/api/auth/dev/login", { clinicianId }),
  logout: () => post<{ ok: true }>("/api/auth/logout"),

  enrolOptions: (token: string) =>
    call<{ options: PublicKeyCredentialCreationOptionsJSON; clinician: { name: string; title: string } }>(
      `/api/auth/enrol/options?token=${encodeURIComponent(token)}`
    ),
  enrolVerify: (token: string, response: unknown) =>
    post<{ ok: true }>("/api/auth/enrol/verify", { token, response }),
  loginOptions: () =>
    call<{ options: PublicKeyCredentialRequestOptionsJSON }>("/api/auth/login/options"),
  loginVerify: (response: unknown) => post<{ ok: true }>("/api/auth/login/verify", { response }),

  picklists: () => call<Picklists>("/api/picklists"),
  onCall: () => call<{ services: OnCallEntry[] }>("/api/on-call"),
  schedule: (from: Date, to: Date) =>
    call<{ shifts: ScheduleShift[] }>(`/api/on-call/schedule?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`),
  pages: () => call<{ incoming: Page[]; outgoing: Page[] }>("/api/pages"),
  page: (id: string) => call<{ page: Page }>(`/api/pages/${id}`),
  createPage: (body: unknown) => post<{ page: Page }>("/api/pages", body),

  hide: (id: string, kind: "archived" | "deleted") => post<{ ok: true }>(`/api/pages/${id}/hide`, { kind }),
  unhide: (id: string) => post<{ ok: true }>(`/api/pages/${id}/unhide`),
  messages: (id: string) => call<{ messages: PageMessage[] }>(`/api/pages/${id}/messages`),
  sendMessage: (id: string, body: string) => post<{ message: PageMessage }>(`/api/pages/${id}/messages`, { body }),

  seen: (id: string) => post<{ page: Page }>(`/api/pages/${id}/seen`),
  accept: (id: string) => post<{ page: Page }>(`/api/pages/${id}/accept`),
  decline: (id: string, outcomeId: string) => post<{ page: Page }>(`/api/pages/${id}/decline`, { outcomeId }),
  complete: (id: string, outcomeId: string | null, feeCode: string | null = null) =>
    post<{ page: Page }>(`/api/pages/${id}/complete`, { outcomeId, feeCode }),
  setFeeCode: (id: string, feeCode: string | null) => post<{ page: Page }>(`/api/pages/${id}/billing`, { feeCode }),
  billing: (from: Date, to: Date) =>
    call<{ items: { page: Page; completedAt: string; outcome: string | null }[] }>(
      `/api/billing?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`),
  search: (f: Search) => {
    const qs = new URLSearchParams(Object.entries(f).filter(([, v]) => v) as [string, string][]);
    return call<{ pages: Page[]; capped: boolean }>(`/api/pages/search?${qs}`);
  },
  cancel: (id: string, outcomeId: string) => post<{ page: Page }>(`/api/pages/${id}/cancel`, { outcomeId }),
  repage: (id: string) => post<{ page: Page }>(`/api/pages/${id}/repage`),
  photos: () => call<{ photos: Record<string, number> }>("/api/photos"),
  setMyPhoto: (dataUrl: string) => post<{ ok: true }>("/api/me/photo", { dataUrl }),
  removeMyPhoto: () => post<{ ok: true }>("/api/me/photo/remove"),
  setPhotoFor: (id: string, dataUrl: string) => post<{ ok: true }>(`/api/admin/people/${id}/photo`, { dataUrl }),
  removePhotoFor: (id: string) => post<{ ok: true }>(`/api/admin/people/${id}/photo/remove`),
  saveProfile: (body: { email: string; phone: string; phoneAlt: string }) => post<{ actor: Actor }>("/api/me/profile", body),
  redirect: (id: string, body: { toId: string | null; service: string; subService: string | null }) => post<{ page: Page }>(`/api/pages/${id}/redirect`, body),

  teams: () => call<{ teams: Team[] }>("/api/teams"),
  clinicians: () => call<{ clinicians: { id: string; name: string; title: string; service: string }[] }>("/api/clinicians"),
  saveTeam: (id: string | null, body: { name: string; description: string; memberIds: string[] }) =>
    post<{ team: Team }>(id ? `/api/teams/${id}` : "/api/teams", body),
  retireTeam: (id: string) => post<{ ok: true }>(`/api/teams/${id}/retire`),
  leaveTeam: (id: string) => post<{ ok: true }>(`/api/teams/${id}/leave`),

  admin: {
    people: () => call<{ people: AdminPerson[] }>("/api/admin/people"),
    savePerson: (id: string | null, body: Omit<AdminPerson, "id" | "upn" | "hasPasskey">) =>
      post<{ ok?: true; id?: string }>(id ? `/api/admin/people/${id}` : "/api/admin/people", body),
    enrolLink: (id: string) => post<{ link: string; expiresInDays: number }>(`/api/admin/people/${id}/enrol`),
    rota: (from: Date, to: Date) =>
      call<{ rows: AdminRotaRow[] }>(`/api/admin/rota?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`),
    addRota: (body: { service: string; subService: string | null; clinicianId: string; position: number; shiftId: string; startDate: string; days: number }) =>
      post<{ added: number }>("/api/admin/rota", body),
    removeRota: (id: string) => post<{ ok: true }>(`/api/admin/rota/${id}/delete`),
    saveDivision: (body: { name: string; grp: string; short: string }) => post<{ ok: true }>("/api/admin/divisions", body),
    retireDivision: (name: string, retired = true) => post<{ ok: true }>("/api/admin/divisions/retire", { name, retired }),
    saveService: (division: string, name: string, retired = false) => post<{ ok: true }>("/api/admin/services", { division, name, retired }),
    saveFeeCode: (code: string, label: string, retired = false) => post<{ ok: true }>("/api/admin/fee-codes", { code, label, retired }),
    shifts: () => call<{ shifts: AdminShift[] }>("/api/admin/shifts"),
    saveShift: (id: string, body: { label: string; starts: string; ends: string }) => post<{ ok: true }>(`/api/admin/shifts/${id}`, body),
  },

  subscribePush: (sub: PushSubscriptionJSON) => post<{ ok: true }>("/api/push/subscribe", sub),
};

// Minimal shapes, so the app does not depend on lib.dom versions for these.
export type PublicKeyCredentialCreationOptionsJSON = Record<string, unknown>;
export type PublicKeyCredentialRequestOptionsJSON = Record<string, unknown>;
